/**
 * Registro en vivo (§4.1) — la lógica compartida por las dos variantes.
 *
 * El requisito que manda: **el usuario registra entre series, de pie, con una mano.** De ahí sale
 * todo lo de acá:
 * - cada ejercicio llega con su peso y reps precargados de la última vez;
 * - registrar una serie idéntica no necesita escribir nada;
 * - cada serie se guarda al tocarla, en local, sin botón de "guardar sesión".
 *
 * Y dos cosas que la pantalla no puede resolver sola, así que se deciden acá:
 * - el ＋ **nunca** se queda sin efecto: cuando no hay serie anterior devuelve `sin_referencia`
 *   para que la UI pida el peso, en vez de precargar un 0 que se guardaría sin chistar;
 * - lo omitido se **excluye** del contador, nunca vale cero.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  calcularCalendario,
  detectarEstancamiento,
  incrementoDe,
  proximaSesion,
  sugerirCarga,
  sugerirReorden,
  ultimaMarca,
  validarSerie,
  volumen,
  type Ejercicio,
  type Fecha,
  type Maquina,
  type MotivoOmision,
  type Resultado,
  type Serie,
  type Sesion,
  type Sugerencia,
  type Unidad,
} from '@mg/core';

import { ErrorAPI } from '../datos/api.ts';
import { db, guardarLocal } from '../datos/db.ts';
import {
  abrirSesion,
  agregarEjercicioASesion,
  anotarUbicacionDeMaquina,
  cerrarSesion,
  omitirEjercicio,
  quitarEjercicioDeSesion,
  registrarSerie,
} from './acciones.ts';
import { useDatos } from './useDatos.ts';

/**
 * Los motivos para no hacer un ejercicio, en un solo lugar. Estaban copiados en las dos variantes
 * —y con etiquetas distintas para la misma cosa— mientras el resumen de cierre necesitaba una
 * tercera copia.
 */
export const MOTIVOS: { valor: MotivoOmision; etiqueta: string }[] = [
  { valor: 'equipo_ocupado', etiqueta: 'Equipo ocupado' },
  { valor: 'dolor', etiqueta: 'Me dolió' },
  { valor: 'sin_tiempo', etiqueta: 'Sin tiempo' },
  { valor: 'decision', etiqueta: 'Lo salteé a propósito' },
];

/**
 * Los motivos que SÍ se arrastran a la próxima sesión (§4.3). Vive acá porque lo usan tres
 * lugares que tienen que decir lo mismo: el filtro de `abrirCon`, que es el que realmente
 * arrastra; el resumen de cierre, que lo promete; y la ayuda de la hoja, que lo explica.
 *
 * El default de `arrastrarSeries` en core es el mismo par. Dolor y decisión no se arrastran:
 * volver a ofrecer mañana lo que hoy dolió sería la app decidiendo por el usuario.
 */
export const MOTIVOS_ARRASTRABLES: MotivoOmision[] = ['equipo_ocupado', 'sin_tiempo'];

export const seArrastra = (motivo: MotivoOmision | null): boolean =>
  motivo !== null && MOTIVOS_ARRASTRABLES.includes(motivo);

/** El motivo dicho dentro de una frase. En pantalla nunca va el código crudo (`equipo_ocupado`). */
export function motivoHumano(motivo: MotivoOmision | null): string {
  switch (motivo) {
    case 'equipo_ocupado':
      return 'equipo ocupado';
    case 'dolor':
      return 'dolor';
    case 'sin_tiempo':
      return 'sin tiempo';
    case 'decision':
      return 'por decisión';
    default:
      return 'sin motivo';
  }
}

/**
 * Lo que el ＋ va a escribir, y con lo que abre la hoja del ejercicio.
 *
 * Existe porque los tres números no cerraban: la tarjeta sugería "45 lb × 8", el ＋ registraba
 * 40 × 12 y la hoja abría en 40 × 8. Ninguno mentía por separado y el conjunto era incomprensible.
 * Ahora sale de acá: la sugerencia de subir se dice aparte, como consejo, no como valor.
 */
export interface ProximaSerie {
  /**
   * null la primera vez con el ejercicio: §6.2 prohíbe dar un número sin historial, y un 0
   * precargado se guarda sin que nada se queje. La UI tiene que pedirlo.
   */
  peso: number | null;
  reps: number;
  unidad: Unidad;
  origen: 'hoy' | 'historial' | 'sin_referencia';
}

export interface EjercicioEnSesion {
  ejercicio: Ejercicio;
  maquina: Maquina | null;
  incremento: number;
  unidad: Unidad;
  completadas: Serie[];
  pendientes: Serie[];
  omitida: Serie | null;
  /** Viene arrastrado de una sesión anterior (§4.3). */
  arrastradoDe: string | null;
  sugerencia: Sugerencia;
  /** Lo que registra el ＋ y con lo que abre la hoja. Es el mismo par en los dos lados. */
  proxima: ProximaSerie;
  estancado: boolean;
  seriesObjetivo: number;
  repsObjetivo: [number, number] | null;
}

/**
 * Qué pasó al tocar el ＋. Antes esto era `void` y el botón podía no hacer absolutamente nada: sin
 * historial no había peso que repetir y la función volvía en silencio, justo en la primera sesión,
 * donde TODOS los ejercicios están en esa situación.
 */
export type ResultadoDelMas =
  | { tipo: 'registrada'; resultado: Resultado }
  /** No hay serie anterior de la cual copiar: la pantalla tiene que pedir el peso. */
  | { tipo: 'sin_referencia' };

/** Un ejercicio que quedó afuera, con lo único que hay que saber de él: por qué, y si vuelve. */
export interface Omitido {
  nombre: string;
  motivo: string;
  /** §4.3 — se vuelve a ofrecer al abrir la próxima sesión. El resumen tiene que decirlo. */
  seArrastra: boolean;
}

/** El cierre de la sesión, dicho con números. Es lo que se ve después de cerrar. */
export interface ResumenSesion {
  seriesCompletadas: number;
  volumenKg: number;
  omitidos: Omitido[];
  /** Lo que sigue. Sin plan no hay fecha, pero el día de la rotación se sabe igual. */
  proxima: { fecha: Fecha | null; nombre: string } | null;
}

export function useRegistro() {
  const datos = useDatos();
  const [abriendo, setAbriendo] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sesion = datos.sesionEnCurso ?? (datos.sesionDeHoy?.estado === 'completada' ? datos.sesionDeHoy : null);

  /**
   * Cuántos ejercicios trae un día de rutina, y cuántos se perdieron por el camino.
   *
   * `useDatos` arma `ejerciciosPorDia` resolviendo cada `rutina_ejercicio` contra el catálogo y
   * **descarta en silencio** los que no encuentra. Un día puede así quedar en cero teniendo filas
   * en la rutina, y hasta ahora eso terminaba en una sesión vacía sin ninguna explicación.
   */
  const contarDia = useCallback(
    (diaId: string | null): { resueltos: number; enRutina: number } => {
      if (!diaId) return { resueltos: 0, enRutina: 0 };
      return {
        resueltos: (datos.ejerciciosPorDia.get(diaId) ?? []).length,
        enRutina: datos.rutinaEjercicios.filter((re) => re.rutina_dia_id === diaId).length,
      };
    },
    [datos.ejerciciosPorDia, datos.rutinaEjercicios],
  );

  /**
   * La última sesión que se cerró. Es la que define la rotación y la que dice si esta persona
   * ya entrenó alguna vez: sin ninguna, hablar de "tu última sesión" es hablar de algo que no
   * existe.
   */
  const ultimaCompletada = useMemo(
    () => datos.sesiones.find((s) => s.estado === 'completada') ?? null,
    [datos.sesiones],
  );

  /** Qué día de rutina toca, si no hay sesión abierta. */
  const diaSugerido = useMemo(() => {
    if (!ultimaCompletada?.rutina_dia_id || datos.rutinaDias.length === 0) return datos.rutinaDias[0] ?? null;
    const i = datos.rutinaDias.findIndex((d) => d.id === ultimaCompletada.rutina_dia_id);
    return datos.rutinaDias[(i + 1) % datos.rutinaDias.length] ?? null;
  }, [ultimaCompletada, datos.rutinaDias]);

  /**
   * Abre la sesión de hoy. `ultimaCompletada` se pasa explícita porque al cerrar una sesión vieja
   * y abrir la de hoy en un toque, `datos` todavía trae el estado anterior: Dexie reemite después
   * de la escritura, y para entonces ya decidimos qué día toca y qué se arrastra.
   */
  const abrirCon = useCallback(
    async (diaId: string | null, ultimaCompletada: Sesion | null): Promise<void> => {
      if (!datos.perfil || !datos.gimnasioActivo) {
        setError('Falta configurar el gimnasio antes de registrar.');
        return;
      }
      setAbriendo(true);
      setError(null);
      try {
        const ejercicios = diaId ? (datos.ejerciciosPorDia.get(diaId) ?? []).map((e) => e.id) : [];

        // §4.3 — lo omitido por causas ajenas en la última sesión completada se arrastra.
        const omitidasPrevias = ultimaCompletada
          ? datos.series.filter(
              (s) => s.sesion_id === ultimaCompletada.id && !s.completada && seArrastra(s.motivo_omision),
            )
          : [];

        await abrirSesion({
          userId: datos.perfil.id,
          planId: datos.planActivo?.id ?? null,
          gimnasioId: datos.gimnasioActivo.id,
          rutinaDiaId: diaId,
          fecha: datos.hoy,
          ejerciciosPlanificados: ejercicios,
          seriesObjetivo: 3,
          omitidasPrevias,
        });
      } catch (e) {
        setError(e instanceof Error ? e.message : 'No se pudo abrir la sesión.');
      } finally {
        setAbriendo(false);
      }
    },
    [datos],
  );

  const abrir = useCallback(
    async (rutinaDiaId?: string): Promise<void> => {
      await abrirCon(rutinaDiaId ?? diaSugerido?.id ?? null, ultimaCompletada);
    },
    [abrirCon, diaSugerido, ultimaCompletada],
  );

  const ejercicios = useMemo<EjercicioEnSesion[]>(() => {
    if (!sesion) return [];

    const deLaSesion = datos.series.filter((s) => s.sesion_id === sesion.id);
    const orden = sesion.rutina_dia_id
      ? (datos.ejerciciosPorDia.get(sesion.rutina_dia_id) ?? []).map((e) => e.id)
      : [];

    // Los ejercicios de la rutina primero, en su orden; después los arrastrados o agregados.
    const ids = [...new Set([...orden, ...deLaSesion.map((s) => s.ejercicio_id)])];

    // Historial SIN esta sesión: la sugerencia tiene que mirar lo anterior, no lo de hoy.
    const historialPrevio = datos.contextos.filter((c) => c.sesion_id !== sesion.id);

    const reEnDia = sesion.rutina_dia_id
      ? datos.rutinaEjercicios.filter((re) => re.rutina_dia_id === sesion.rutina_dia_id)
      : [];

    return ids
      .map((id) => {
        const ejercicio = datos.ejerciciosPorId.get(id);
        if (!ejercicio) return null;

        const maquina = datos.maquinaDe(id);
        const suyas = deLaSesion.filter((s) => s.ejercicio_id === id);
        const completadas = suyas.filter((s) => s.completada).sort((a, b) => a.numero_serie - b.numero_serie);
        const pendientes = suyas
          .filter((s) => !s.completada && s.motivo_omision === null)
          .sort((a, b) => a.numero_serie - b.numero_serie);
        const omitida = suyas.find((s) => !s.completada && s.motivo_omision !== null) ?? null;
        const arrastrada = suyas.find((s) => s.arrastrada_de_sesion_id !== null);

        const config = reEnDia.find((re) => re.ejercicio_id === id);
        const repsObjetivo: [number, number] | null = config
          ? [config.reps_objetivo_min, config.reps_objetivo_max]
          : ejercicio.rango_reps;

        const sugerencia = sugerirCarga({
          ejercicio,
          historial: historialPrevio,
          maquina,
          rangoReps: repsObjetivo,
        });

        const unidad: Unidad = sugerencia.unidad;

        // Qué repite el ＋, en orden de cercanía: lo último de hoy, lo último que se registró
        // alguna vez, o nada. "Nada" es un caso real y frecuente —toda la primera sesión— y por
        // eso viaja explícito en vez de colarse como un 0.
        const marca = ultimaMarca(historialPrevio, id);
        const ultimaDeHoy = completadas[completadas.length - 1] ?? null;
        const proxima: ProximaSerie = ultimaDeHoy
          ? { peso: ultimaDeHoy.peso, reps: ultimaDeHoy.reps, unidad: ultimaDeHoy.unidad, origen: 'hoy' }
          : marca
            ? {
                peso: marca.referencia.peso,
                reps: marca.referencia.reps,
                unidad: marca.referencia.unidad,
                origen: 'historial',
              }
            : {
                peso: null,
                reps: sugerencia.repsSugeridas ?? repsObjetivo?.[0] ?? 10,
                unidad,
                origen: 'sin_referencia',
              };

        return {
          ejercicio,
          maquina,
          incremento: incrementoDe(maquina, unidad),
          unidad,
          completadas,
          pendientes,
          omitida,
          arrastradoDe: arrastrada?.arrastrada_de_sesion_id ?? null,
          sugerencia,
          proxima,
          estancado: detectarEstancamiento(historialPrevio, id, 3) !== null,
          seriesObjetivo: config?.series_objetivo ?? Math.max(3, completadas.length + pendientes.length),
          repsObjetivo,
        } satisfies EjercicioEnSesion;
      })
      .filter((x): x is EjercicioEnSesion => x !== null);
  }, [sesion, datos]);

  /** §6.3 — si ayer se cargó el mismo músculo, se propone reordenar la sesión. */
  const reorden = useMemo(() => {
    if (!sesion?.rutina_dia_id) return null;
    const ayer = datos.sesiones.find(
      (s) => s.estado === 'completada' && s.id !== sesion.id && s.fecha < sesion.fecha,
    );
    if (!ayer?.rutina_dia_id) return null;

    const diff = (new Date(`${sesion.fecha}T00:00:00Z`).getTime() - new Date(`${ayer.fecha}T00:00:00Z`).getTime()) / 86_400_000;
    if (diff !== 1) return null;

    const hoyEjs = datos.ejerciciosPorDia.get(sesion.rutina_dia_id) ?? [];
    const ayerEjs = datos.ejerciciosPorDia.get(ayer.rutina_dia_id) ?? [];
    return sugerirReorden(hoyEjs, ayerEjs);
  }, [sesion, datos]);

  const registrar = useCallback(
    async (item: EjercicioEnSesion, valores: { peso: number; reps: number; unidad: Unidad; rir?: number | null }): Promise<Resultado> => {
      if (!sesion) return { ok: false, requiereConfirmacion: false, hallazgos: [] };

      // Una sesión cerrada no acepta series. Antes el chequeo no existía: alcanzaba con tocar el
      // nombre del ejercicio para seguir registrando en una sesión que ya se había cerrado.
      if (sesion.estado === 'completada') {
        return {
          ok: false,
          requiereConfirmacion: false,
          hallazgos: [
            {
              campo: 'sesion',
              severidad: 'error',
              codigo: 'sesion_cerrada',
              mensaje: 'La sesión está cerrada. Reabrila para agregar una serie.',
            },
          ],
        };
      }

      const validacion = validarSerie(
        { reps: valores.reps, peso: valores.peso, unidad: valores.unidad },
        item.maquina,
      );
      if (!validacion.ok) return validacion;

      await registrarSerie({
        sesionId: sesion.id,
        ejercicioId: item.ejercicio.id,
        reps: valores.reps,
        peso: valores.peso,
        unidad: valores.unidad,
        maquinaId: item.maquina?.id ?? null,
        rir: valores.rir ?? null,
        pendiente: item.pendientes[0] ?? null,
      });

      return validacion;
    },
    [sesion],
  );

  /**
   * El camino de un toque: repetir exactamente la serie anterior de este ejercicio.
   *
   * Pasa siempre por `registrar`, que completa la serie PENDIENTE si queda alguna. Antes creaba
   * una serie nueva por su cuenta: el objetivo crecía con lo hecho (1/3 → 2/4 → 3/5) y la fila
   * nueva nacía con la misma `idem_key` que la pendiente que había quedado viva.
   */
  const repetir = useCallback(
    async (item: EjercicioEnSesion): Promise<ResultadoDelMas> => {
      const { peso, reps, unidad } = item.proxima;
      if (peso === null) return { tipo: 'sin_referencia' };
      return { tipo: 'registrada', resultado: await registrar(item, { peso, reps, unidad }) };
    },
    [registrar],
  );

  const omitir = useCallback(
    async (item: EjercicioEnSesion, motivo: MotivoOmision): Promise<void> => {
      if (!sesion) return;
      await omitirEjercicio(sesion.id, item.ejercicio.id, motivo);
    },
    [sesion],
  );

  /** Ejercicios del catálogo que todavía no están en la sesión. Es lo que se puede agregar. */
  const agregables = useMemo(() => {
    const yaEstan = new Set(ejercicios.map((e) => e.ejercicio.id));
    return datos.ejercicios
      .filter((e) => !yaEstan.has(e.id))
      .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
  }, [ejercicios, datos.ejercicios]);

  const agregar = useCallback(
    async (ejercicioId: string, cuantas = 3): Promise<void> => {
      if (!sesion) return;
      await agregarEjercicioASesion(sesion.id, ejercicioId, cuantas);
    },
    [sesion],
  );

  const quitar = useCallback(
    async (item: EjercicioEnSesion): Promise<void> => {
      if (!sesion) return;
      await quitarEjercicioDeSesion(sesion.id, item.ejercicio.id);
    },
    [sesion],
  );

  /**
   * Por qué la sesión no tiene un solo ejercicio, dicho con precisión. Son dos causas distintas y
   * se arreglan en lugares distintos: un día sin ejercicios se edita en la rutina; un día cuyos
   * ejercicios no existen en el catálogo es un dato roto y hay que decirlo, no tragárselo.
   */
  const sesionVacia = useMemo<string | null>(() => {
    if (!sesion || ejercicios.length > 0) return null;

    if (!sesion.rutina_dia_id) {
      return 'Esta sesión se abrió sin un día de rutina, así que no trajo ningún ejercicio.';
    }

    const nombre = datos.rutinaDias.find((d) => d.id === sesion.rutina_dia_id)?.nombre ?? 'Ese día';
    const { enRutina } = contarDia(sesion.rutina_dia_id);

    return enRutina > 0
      ? `«${nombre}» tiene ${enRutina} ${enRutina === 1 ? 'ejercicio' : 'ejercicios'} en tu rutina, pero ${enRutina === 1 ? 'no está' : 'ninguno está'} en el catálogo de este gimnasio, así que no se pudo cargar. Agregalos acá para hoy, o arreglá la rutina con el botón de abajo.`
      : `«${nombre}» no tiene ningún ejercicio cargado en tu rutina.`;
  }, [sesion, ejercicios, datos.rutinaDias, contarDia]);

  const cerrar = useCallback(async (): Promise<void> => {
    if (!sesion) return;
    // Las series que quedaron pendientes al cerrar se descartan: una fila "no la hice" sin
    // motivo no aporta nada, y §4.3 sólo pide registrar la omisión cuando tiene un motivo.
    const pendientes = await db.series
      .where('sesion_id')
      .equals(sesion.id)
      .filter((s) => !s.completada && s.motivo_omision === null)
      .toArray();
    if (pendientes.length > 0) await db.series.bulkDelete(pendientes.map((s) => s.id));
    await cerrarSesion(sesion);
  }, [sesion]);

  /**
   * Vuelve a abrir una sesión cerrada. §4.2 parte de que equivocarse es normal: cerrar la sesión y
   * después acordarse de una serie es exactamente eso, y obligar a crear una sesión nueva para el
   * mismo día ensuciaría el conteo del plan.
   */
  const reabrir = useCallback(async (): Promise<void> => {
    if (!sesion || sesion.estado !== 'completada') return;
    await guardarLocal('sesiones', [{ ...sesion, estado: 'en_curso', cerrada_en: null }]);
  }, [sesion]);

  /**
   * Cierra la sesión que quedó abierta de otro día y abre la de hoy en un solo toque. La sesión en
   * curso no se cierra sola al terminar el día (deuda conocida de `docs/flujo.md`), así que el
   * usuario se encuentra con la de anteayer y hasta ahora no tenía ninguna salida corta.
   */
  const cerrarYEmpezarHoy = useCallback(async (): Promise<void> => {
    if (!sesion || sesion.fecha === datos.hoy) return;
    await cerrar();
    const i = datos.rutinaDias.findIndex((d) => d.id === sesion.rutina_dia_id);
    const siguiente =
      i >= 0 && datos.rutinaDias.length > 0 ? datos.rutinaDias[(i + 1) % datos.rutinaDias.length] : diaSugerido;
    await abrirCon(siguiente?.id ?? null, { ...sesion, estado: 'completada' });
  }, [sesion, datos.hoy, datos.rutinaDias, cerrar, abrirCon, diaSugerido]);

  const totalCompletadas = ejercicios.reduce((a, e) => a + e.completadas.length, 0);

  /**
   * Lo omitido se EXCLUYE, nunca vale cero: un ejercicio que no se pudo hacer no puede dejar la
   * sesión en "12 / 15" para siempre. De un ejercicio omitido sólo cuentan las series que sí
   * llegaron a hacerse antes de omitirlo.
   */
  const totalPlanificadas = ejercicios.reduce(
    (a, e) => a + (e.omitida ? e.completadas.length : Math.max(e.seriesObjetivo, e.completadas.length)),
    0,
  );

  const omitidos = useMemo<Omitido[]>(
    () =>
      ejercicios
        .filter((e) => e.omitida !== null)
        .map((e) => ({
          nombre: e.ejercicio.nombre,
          motivo: motivoHumano(e.omitida!.motivo_omision),
          seArrastra: seArrastra(e.omitida!.motivo_omision),
        })),
    [ejercicios],
  );

  /** Series que se van a descartar al cerrar. Se dice ANTES, junto al botón, no después. */
  const seriesPendientes = ejercicios.reduce((a, e) => a + (e.omitida ? 0 : e.pendientes.length), 0);

  /**
   * El resumen del cierre. Se calcula sólo con la sesión cerrada porque el calendario no es
   * gratis y es lo único que lo pide.
   */
  const resumen = useMemo<ResumenSesion | null>(() => {
    if (!sesion || sesion.estado !== 'completada') return null;

    const proxima = (() => {
      if (datos.planActivo && datos.gimnasioActivo) {
        const entrada = proximaSesion(
          calcularCalendario({
            plan: datos.planActivo,
            gimnasio: datos.gimnasioActivo,
            diasBloqueados: datos.diasBloqueados,
            rutinaDias: datos.rutinaDias,
            ejerciciosPorDia: datos.ejerciciosPorDia,
            sesiones: datos.sesiones,
            hoy: datos.hoy,
          }),
        );
        if (entrada) return { fecha: entrada.fecha, nombre: entrada.nombre };
      }
      // Sin plan no hay fecha que prometer, pero el día de la rotación se sabe igual.
      return diaSugerido ? { fecha: null, nombre: diaSugerido.nombre } : null;
    })();

    return {
      seriesCompletadas: totalCompletadas,
      volumenKg: volumen(
        datos.contextos.filter((c) => c.sesion_id === sesion.id),
        'kg',
      ),
      omitidos,
      proxima,
    };
  }, [sesion, datos, ejercicios, diaSugerido, totalCompletadas, omitidos]);

  return {
    datos,
    sesion,
    diaSugerido,
    abrir,
    abriendo,
    error,
    ejercicios,
    reorden,
    registrar,
    repetir,
    omitir,
    agregables,
    agregar,
    quitar,
    sesionVacia,
    cerrar,
    cerrarYEmpezarHoy,
    reabrir,
    totalCompletadas,
    totalPlanificadas,
    omitidos,
    seriesPendientes,
    resumen,
    /**
     * Nunca se cerró una sesión: no hay "última sesión" de la cual venir por rotación, así que
     * la pantalla previa no puede hablar de una. Es el día 1 de la rutina.
     */
    esPrimerDia: ultimaCompletada === null,
    /** La sesión abierta es de hoy. Si no, es una que quedó sin cerrar y hay que decirlo. */
    esDeHoy: sesion ? sesion.fecha === datos.hoy : true,
    cerrada: sesion?.estado === 'completada',
    /** Cuántos ejercicios trae el día que se va a abrir. Sirve para avisar ANTES de abrirlo. */
    conteoDiaSugerido: contarDia(diaSugerido?.id ?? null),
    cardioDeLaSesion: sesion ? datos.cardio.find((c) => c.sesion_id === sesion.id) ?? null : null,
  };
}

/**
 * Anotar dónde está la máquina de un ejercicio, desde la pantalla de registro.
 *
 * Es lo que le faltaba a "Ubicación sin registrar": un dato faltante que se veía en cada tarjeta
 * y no ofrecía ninguna salida. La lógica vive acá —y no en las dos variantes— porque las dos
 * tienen que validar igual, traducir el error de red igual y decir lo mismo.
 *
 * Esto NO es local-first: el mapa de máquinas es del gimnasio, no del usuario, y va directo al
 * servidor. Sin señal no se puede aportar, y hay que decirlo en vez de fingir que se guardó.
 */
export type EstadoAporte =
  | { fase: 'listo' }
  | { fase: 'guardando' }
  | { fase: 'error'; mensaje: string };

export function useAnotarUbicacion(gimnasioId: string | null): {
  estado: EstadoAporte;
  /** Devuelve la etiqueta guardada, o null si no se guardó. La UI confirma con lo que devuelve. */
  guardar: (v: {
    ejercicioId: string;
    etiqueta: string;
    unidad: Unidad;
    /** Tal como lo escribió el usuario: puede venir vacío ("no sé de cuánto sube"). */
    incrementoTexto: string;
  }) => Promise<string | null>;
  limpiar: () => void;
} {
  const [estado, setEstado] = useState<EstadoAporte>({ fase: 'listo' });

  const guardar = useCallback(
    async (v: {
      ejercicioId: string;
      etiqueta: string;
      unidad: Unidad;
      incrementoTexto: string;
    }): Promise<string | null> => {
      const etiqueta = v.etiqueta.trim();
      if (!gimnasioId) {
        setEstado({
          fase: 'error',
          mensaje: 'Falta un gimnasio activo: una ubicación es de un gimnasio, no del ejercicio.',
        });
        return null;
      }
      if (etiqueta === '') {
        setEstado({ fase: 'error', mensaje: 'Escribí cómo la reconocés. Ej.: "Máquina 3, pared del fondo".' });
        return null;
      }

      const crudo = Number(v.incrementoTexto.trim().replace(',', '.'));
      const incremento = v.incrementoTexto.trim() !== '' && Number.isFinite(crudo) && crudo > 0 ? crudo : null;

      setEstado({ fase: 'guardando' });
      try {
        await anotarUbicacionDeMaquina({
          gimnasioId,
          ejercicioId: v.ejercicioId,
          etiqueta,
          unidad: v.unidad,
          incremento,
        });
        setEstado({ fase: 'listo' });
        return etiqueta;
      } catch (e) {
        // El mensaje genérico de la red ("queda guardado y sube después") sería mentira acá.
        const sinRed = e instanceof ErrorAPI && (e.codigo === 'sin_red' || e.codigo === 'timeout');
        setEstado({
          fase: 'error',
          mensaje: sinRed
            ? 'Sin conexión. El mapa del gimnasio es compartido y se guarda en el servidor: no quedó anotada. Probá de nuevo cuando vuelva la señal.'
            : e instanceof Error
              ? e.message
              : 'No se pudo anotar la ubicación.',
        });
        return null;
      }
    },
    [gimnasioId],
  );

  return { estado, guardar, limpiar: useCallback(() => setEstado({ fase: 'listo' }), []) };
}

/**
 * Temporizador de descanso (§4.1): arranca solo al registrar una serie, y es opcional. Cuenta
 * hacia arriba, no hacia abajo: no hay un descanso "correcto" que la app deba imponer, sólo
 * interesa cuánto pasó.
 */
export function useTemporizador() {
  const [desde, setDesde] = useState<number | null>(null);
  const [segundos, setSegundos] = useState(0);
  const reloj = useRef<number | null>(null);

  useEffect(() => {
    if (desde === null) {
      setSegundos(0);
      return;
    }
    const tick = () => setSegundos(Math.floor((Date.now() - desde) / 1000));
    tick();
    reloj.current = window.setInterval(tick, 1000);
    return () => {
      if (reloj.current !== null) window.clearInterval(reloj.current);
    };
  }, [desde]);

  return {
    segundos,
    corriendo: desde !== null,
    arrancar: () => setDesde(Date.now()),
    parar: () => setDesde(null),
  };
}
